'use client';

import React, { useCallback, useState, useEffect, useRef } from 'react';
import { toast } from '@/hooks/shared/use-toast';
import { useAuth } from '@/providers/AuthProvider';
import { TrendingUp } from 'lucide-react';
import { rawColorPalette } from '../utils/sensorDataColorMap';
import { SelectionModeCard } from './charts/SelectionModeCard';
import { IntervalDialog } from './dialogs/IntervalDialog';
import { HarmonicGroupMenu } from './dialogs/HarmonicGroupMenu';
import { HarmonicEditDialog } from './dialogs/HarmonicEditDialog';
import { SideBandsGroupEditDialog } from './dialogs/SideBandsGroupEditDialog';
import { VerticalLineMenu } from './dialogs/VerticalLineMenu';
import { CustomVerticalLineMenu } from './dialogs/CustomVerticalLineMenu';
import { DataLayerSection } from './DataLayerSection';
import { DrawingProvider } from './drawing';
import {
  useEnabledSensors,
  useVerticalLines,
  useIntervalLines,
  useSelectionMode,
  useDialogState,
  useChartSections,
  useFeatureManager,
  useSelectionHandlers,
  useSharedTimeRangeController,
  useChartIds,
  useFetchData,
  useSectionInteractionHandlers,
  useSectionFilterHandlers,
  useToolsSyncState,
  useBenchmarkLines,
  useComparisonTableState,
  useVerticalLineMenus,
  useHarmonicManagement,
  useSideBandsManagement,
  useFocusMode,
  useVerticalLineValues,
  useAutoBenchmark,
  useHighlightedVerticalLines,
  createExcelExport,
  useDataLayerReset,
  useSensorToggleRegistration,
  useLoadingSensorCleanup,
  useSensorDataRegistration,
  useForceRefresh,
  useRequestCleanup,
  useHarmonicGroupMenuHandlers,
  useVerticalLineMenuHandlers,
  useHarmonicEditDialogHandler,
  useSideBandsEditDialogHandler,
} from '@/hooks/shared/DataLayers';
import type { GraphDefinition } from '@/store/api/dataLayersApi';
import type { JSX } from 'react/jsx-runtime';
import type { VerticalLine } from '../types';
import { useSectionConfig } from '@/hooks/shared/DataLayers/use-section-config';
import { useMemoizedObject } from '@/hooks/shared/DataLayers/use-memoized-object';

interface DataLayerDashboardContainerProps {
  queryParams: any;
  sections?: any[];
  sectionConfigGraphs?: any;
  enabledSections?: any;
  enabledGraphs?: Record<string, boolean>;
  onResetDataLayer?: () => void;
  resetTrigger?: boolean;
  triggerReset?: boolean;
  onLoadingStateChange?: (isLoading: boolean) => void;
  forceRefresh?: number;
  includeAlarms?: boolean;
  variant?: string;
  verticalLinesState?: any;
  externalLegendStates?: any;
  onLegendToggle?: any;
  onExternalLegendToggle?: any;
  onToggleDataPoint?: any;
  onAddDataPoint?: any;
  onToggleSensor?: any;
  registerDataPointToggleHandler?: any;
  registerDataPointData?: any;
  registerSensorToggleHandler?: any;
  registerSensorData?: any;
  masterSensorColorMap?: Record<string, string>; // Global sensor name -> color mapping for consistency across all data layers
  dataLayer?: string;
  // Harmonic interval configuration per section (default: 'days' for date charts, 1 for numeric charts)
  // Can be time-based ('seconds' | 'minutes' | 'hours' | 'days') or absolute numeric value
  harmonicIntervalConfig?: Record<string, 'seconds' | 'minutes' | 'hours' | 'days' | number>;
  // Whether to preserve markers, harmonics, and side bands lines when resetting the data layer (default: true = keep them)
  preserveVerticalLinesOnReset?: boolean;
  // Feature toggles (default: all false = hidden)
  enableCompareTable?: boolean;
  enableMarkers?: boolean;
  enableHarmonics?: boolean;
  enableSideBands?: boolean;
  enableDrawing?: boolean; // Show drawing tools popover in toolbar
  enableHarmonicEdit?: boolean; // Show edit option in harmonics menu (default: true)
  enableSideBandsEdit?: boolean; // Show edit option in side bands menu (default: true)
  // If true, creating harmonics/side bands will replace existing markers (default: false = keep markers)
  replaceMarkersOnHarmonicCreation?: boolean;
  markerLabel?: string; // Custom label for marker button (e.g., "Timestamps" for sensors, "Markers" for spectrum)
  expandableLegendLabel?: string; // Custom label for expandable legend series (e.g., "Timestamps" for spectrum)
  syncTools?: {
    markers?: boolean; // Sync marker button state across sections
    harmonics?: boolean; // Sync harmonics button state across sections
    sideBands?: boolean; // Sync side bands button state across sections
  };
  syncSectionLegends?: boolean;
  syncLegendComparisonTable?: boolean; // Sync Abs/% toggle across sections (default: true)
  activeDataLayerFilters?: any;
  focusedSensors?: Record<string, string>; // Map of sectionId -> focused sensor name
  focusMode?: boolean;
  onFocus?: (sensorName: string, sectionId?: string) => void;
  onToggleFocusMode?: (sectionId: string) => void; // Toggle focus mode for a specific section
  // Global loading sensors state - synchronized across all sections
  loadingSensors?: Set<string>;
  setLoadingSensor?: (sensorName: string) => void;
  clearLoadingSensor?: (sensorName: string) => void;
  // Direct loading state for timestamps (passed from parent, works in both focus and normal modes)
  isTimestampsLoading?: boolean;
  // Synced tool selection states (external state from parent for performance)
  markerSelectionModeByChart?: Record<string, boolean>;
  setMarkerSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  harmonicSelectionModeByChart?: Record<string, boolean>;
  setHarmonicSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  sideBandsSelectionModeByChart?: Record<string, boolean>;
  setSideBandsSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  customLineModeByChart?: Record<string, string | null>;
  setCustomLineModeByChart?: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  // Selected marker timestamp for highlighting
  selectedMarkerTimestamp?: string | null;
  // Selected timestamps set for multi-select highlighting
  selectedTimestampsSet?: Set<string>;
  // Expandable legend series (timestamp rows beneath sensors)
  expandableLegendSeries?: any[];
  // Popover timestamps (flat list for snapshots dropdown)
  popoverTimestamps?: any[];
  // Callbacks for expandable legend series
  onToggleExpandableSeries?: (seriesId: string) => void;
  onToggleExpandableSeriesFocus?: (seriesId: string) => void;
  onToggleExpandableTimestamp?: (seriesId: string, timestampId: string) => void;
  onRemoveMarker?: (markerId: string) => void; // Delete marker callback for expandable legend series
  // Focus mode for expandable legend series (timestamp multi-select)
  expandableLegendFocusMode?: boolean;
  // Enabled series for filtering which traces to show (e.g., acceleration/velocity in waveform)
  enabledSeries?: Set<string>;
  // Floating toolbar
  showFloatingToolbar?: boolean; // Show floating toolbar over charts instead of top toolbar
  // Shared benchmark state (lifted to parent level to persist across component switches)
  benchmarkLineBySection?: Record<string, string | null>;
  setBenchmarkLineBySection?: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  // Inline focus button visibility
  showInlineFocusButton?: boolean; // Control visibility of inline "Focus" button next to each sensor (default: true)
  showSectionControls?: boolean; // Show or hide the Clear/Reset buttons
  isExpandable?: boolean; // Enables expandable behavior (toggle button vs static title)

  // Expandable legend for API body (sent with data requests, not affecting signature)
  expandableLegendForApi?: any;
  // Y-axis scale options
  showYAxisScale?: boolean;
  enableDynamicMargin?: boolean;
  autoscaleMode?: 'none' | 'onload' | 'onFirstDataLoad' | 'onDataLoad';
  autoscaleAxis?: 'x' | 'y' | 'both';
  // Callback when section data changes (for audio integration in Waveform)
  onDataChange?: (sectionId: string, sectionData: any) => void;
}

type Metric = {
  key: string;
  label: string;
  unit: string;
  show: boolean;
  sensorColors: Record<string, string>;
  icon: JSX.Element;
};

export const DataLayerDashboardContainer = ({
  queryParams,
  sections,
  sectionConfigGraphs,
  enabledSections: externalEnabledSections,
  enabledGraphs,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onResetDataLayer = () => {},
  resetTrigger,
  triggerReset = false,
  onLoadingStateChange,
  forceRefresh = 0,
  includeAlarms,
  variant,
  verticalLinesState,
  externalLegendStates,
  onLegendToggle,
  onExternalLegendToggle,
  onToggleDataPoint,
  onAddDataPoint,
  onToggleSensor,
  registerDataPointToggleHandler,
  registerDataPointData,
  registerSensorToggleHandler,
  registerSensorData,
  masterSensorColorMap,
  dataLayer,
  harmonicIntervalConfig = {},
  preserveVerticalLinesOnReset = true,
  enableCompareTable = false,
  enableMarkers = false,
  enableHarmonics = false,
  enableSideBands = false,
  enableDrawing = false,
  enableHarmonicEdit = true,
  enableSideBandsEdit = true,
  replaceMarkersOnHarmonicCreation = false,
  markerLabel = 'Markers',
  expandableLegendLabel,
  syncTools = {},
  syncSectionLegends,
  syncLegendComparisonTable = true, // Default to true (synced Abs/% buttons)
  activeDataLayerFilters,
  focusedSensors = {},
  focusMode = false,
  onFocus,
  onToggleFocusMode,
  loadingSensors,
  setLoadingSensor,
  clearLoadingSensor,
  isTimestampsLoading,
  selectedMarkerTimestamp,
  selectedTimestampsSet,
  expandableLegendSeries = [],
  expandableLegendFocusMode = false,
  enabledSeries,
  popoverTimestamps,
  onToggleExpandableSeries,
  onToggleExpandableSeriesFocus,
  onToggleExpandableTimestamp,
  onRemoveMarker,
  markerSelectionModeByChart: externalMarkerSelectionModeByChart,
  setMarkerSelectionModeByChart: externalSetMarkerSelectionModeByChart,
  harmonicSelectionModeByChart: externalHarmonicSelectionModeByChart,
  setHarmonicSelectionModeByChart: externalSetHarmonicSelectionModeByChart,
  sideBandsSelectionModeByChart: externalSideBandsSelectionModeByChart,
  setSideBandsSelectionModeByChart: externalSetSideBandsSelectionModeByChart,
  customLineModeByChart: externalCustomLineModeByChart,
  setCustomLineModeByChart: externalSetCustomLineModeByChart,
  showFloatingToolbar = false,
  benchmarkLineBySection: externalBenchmarkLineBySection,
  setBenchmarkLineBySection: externalSetBenchmarkLineBySection,
  showInlineFocusButton = true,
  showSectionControls,
  isExpandable,
  expandableLegendForApi,
  showYAxisScale = false,
  enableDynamicMargin = false,
  autoscaleMode = 'none',
  autoscaleAxis = 'both',
  onDataChange,
}: DataLayerDashboardContainerProps) => {
  const { user } = useAuth();
  const [loadingFeatures, setLoadingFeatures] = useState<Record<string, Record<string, boolean>>>({});

  // Use tools sync state hook (manages marker/harmonic/sideBands selection modes)
  const {
    markerSelectionModeByChart,
    setMarkerSelectionModeByChart,
    harmonicSelectionModeByChart,
    setHarmonicSelectionModeByChart,
    sideBandsSelectionModeByChart,
    setSideBandsSelectionModeByChart,
    customLineModeByChart,
    setCustomLineModeByChart,
  } = useToolsSyncState({
    markerSelectionModeByChart: externalMarkerSelectionModeByChart,
    setMarkerSelectionModeByChart: externalSetMarkerSelectionModeByChart,
    harmonicSelectionModeByChart: externalHarmonicSelectionModeByChart,
    setHarmonicSelectionModeByChart: externalSetHarmonicSelectionModeByChart,
    sideBandsSelectionModeByChart: externalSideBandsSelectionModeByChart,
    setSideBandsSelectionModeByChart: externalSetSideBandsSelectionModeByChart,
    customLineModeByChart: externalCustomLineModeByChart,
    setCustomLineModeByChart: externalSetCustomLineModeByChart,
  });

  // Helper function to get the harmonic interval for a section
  // Default: 'days' for date charts, 1 for numeric charts
  const getHarmonicInterval = useCallback(
    (
      sectionKey: string,
      xAxisType: 'date' | 'numeric' | 'category'
    ): 'seconds' | 'minutes' | 'hours' | 'days' | number => {
      // If explicitly configured, use that
      if (harmonicIntervalConfig[sectionKey] !== undefined) {
        return harmonicIntervalConfig[sectionKey];
      }
      // Otherwise, use intelligent default based on chart type
      return xAxisType === 'date' ? 'days' : 1;
    },
    [harmonicIntervalConfig]
  );

  // Use comparison table state hook (manages percentage/table view toggles)
  const {
    showPercentage,
    setShowPercentage,
    showTableView,
    setShowTableView,
    showPercentageBySection,
    setShowPercentageBySection,
    showTableViewBySection,
    setShowTableViewBySection,
  } = useComparisonTableState({ syncLegendComparisonTable });

  // Use vertical line menus hook (manages menu state and editing dialogs)
  const {
    selectedHarmonicLineId,
    setSelectedHarmonicLineId,
    harmonicMenuPosition,
    setHarmonicMenuPosition,
    selectedVerticalLineId,
    setSelectedVerticalLineId,
    verticalLineMenuPosition,
    setVerticalLineMenuPosition,
    clickedChartId,
    setClickedChartId,
    editingHarmonicGroup,
    setEditingHarmonicGroup,
    editingSideBandsGroup,
    setEditingSideBandsGroup: setEditingCarrierGroup,
    pendingHarmonicCreation,
    setPendingHarmonicCreation,
  } = useVerticalLineMenus();

  // State for custom vertical line menu
  const [customLineMenuState, setCustomLineMenuState] = useState<{
    open: boolean;
    position: { x: number; y: number };
    lineData: any;
  }>({
    open: false,
    position: { x: 0, y: 0 },
    lineData: null,
  });

  const [focusedCustomGroupId, setFocusedCustomGroupId] = useState<string | null>(null);

  const {
    normalizedGraphs,
    sectionConfig,
    availableSections,
    enabledSections,
    splitDefault,
    sensorEnableMap,
    initialFeatures,
  } = useSectionConfig(sectionConfigGraphs || [], enabledGraphs);

  const graphDefinitions: GraphDefinition[] = normalizedGraphs;

  const {
    activeFeatures,
    handleFeatureChange,
    toggleSectionMultiSelect,
    getSectionMultiSelect,
    sectionHasFeatures,
    resetAllFeatures,
    resetFeatures,
  } = useFeatureManager(initialFeatures, sectionConfig, triggerReset);

  const {
    enabledSensors,
    toggleSensor: internalToggleSensor,
    enableSensor: internalEnableSensor,
    disableSensor: internalDisableSensor,
    clearSensors,
    resetSensors,
    forceResetToInitial,
    isSensorLoading,
  } = useEnabledSensors(sensorEnableMap);

  // Sync external legend states to internal enabled sensors for alarms
  // Use a ref to track the last synced state to avoid circular updates
  const lastSyncedStatesRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (!externalLegendStates || !sectionConfigGraphs) {
      console.log('[ALARM_SYNC] Skipping sync - missing data:', JSON.stringify({
        hasExternalStates: !!externalLegendStates,
        hasSectionConfig: !!sectionConfigGraphs
      }, null, 2));
      return;
    }

    console.log('[ALARM_SYNC] Running sync effect with externalLegendStates:', JSON.stringify(externalLegendStates, null, 2));

    // For each section, check if any legend items have changed state
    sectionConfigGraphs.forEach((graph: any) => {
      graph.legend?.forEach((item: any) => {
        if (item.id && externalLegendStates[item.id] !== undefined) {
          const sensorName = item.displayName || item.name;
          const shouldBeEnabled = externalLegendStates[item.id];
          const lastSynced = lastSyncedStatesRef.current[item.id];

          // Check actual state in enabledSensors to detect if state was cleared
          const actuallyEnabled = enabledSensors[graph.id]?.[sensorName] === true;

          console.log('[ALARM_SYNC] Checking item:', JSON.stringify({
            itemId: item.id,
            sensorName,
            shouldBeEnabled,
            lastSynced,
            actuallyEnabled,
            needsUpdate: shouldBeEnabled !== actuallyEnabled
          }, null, 2));

          // BUGFIX: Always sync when external state differs from actual state
          // This ensures re-enabling works even if ref tracking gets out of sync
          if (shouldBeEnabled !== actuallyEnabled) {
            console.log('[ALARM_SYNC] Updating enabledSensor:', JSON.stringify({
              graphId: graph.id,
              sensorName,
              action: shouldBeEnabled ? 'enable' : 'disable',
              reason: lastSynced === undefined ? 'first-sync' :
                      lastSynced === shouldBeEnabled ? 'state-was-cleared' : 'state-changed'
            }, null, 2));

            if (shouldBeEnabled) {
              internalEnableSensor(graph.id, sensorName);
            } else {
              internalDisableSensor(graph.id, sensorName);
            }

            // BUGFIX: Update ref AFTER calling enable/disable to ensure it reflects the intended action
            // This prevents the ref from getting out of sync if state updates don't complete immediately
            lastSyncedStatesRef.current[item.id] = shouldBeEnabled;
          }
        }
      });
    });
  }, [externalLegendStates, sectionConfigGraphs, enabledSensors, internalEnableSensor, internalDisableSensor]);

  // Wrap toggleSensor to notify external handler
  const toggleSensor = useCallback(
    (sectionType: string, sensorName: string) => {
      console.log('[ALARM_TOGGLE_SENSOR] Called:', JSON.stringify({ sectionType, sensorName }, null, 2));

      internalToggleSensor(sectionType, sensorName);
      //activated when removed selected sensor

      // If we have external handler, find the legend item ID and notify
      if (onExternalLegendToggle) {
        const sectionConfig = sectionConfigGraphs?.find((g: any) => g.id === sectionType);
        const legendItem = sectionConfig?.legend?.find((item: any) => item.displayName === sensorName);
        if (legendItem?.id) {
          const newState = !enabledSensors[sectionType]?.[sensorName];
          console.log('[ALARM_TOGGLE_SENSOR] Notifying parent:', JSON.stringify({
            legendItemId: legendItem.id,
            newState,
            currentState: enabledSensors[sectionType]?.[sensorName]
          }, null, 2));
          onExternalLegendToggle(legendItem.id, newState);
        } else {
          console.log('[ALARM_TOGGLE_SENSOR] Legend item not found for sensor:', sensorName);
        }
      }
    },
    [internalToggleSensor, onExternalLegendToggle, sectionConfigGraphs, enabledSensors]
  );

  // Wrap enableSensor to notify external handler
  const enableSensor = useCallback(
    (sectionType: string, sensorName: string) => {
      internalEnableSensor(sectionType, sensorName);
      //activated when add unselected sensor
      //activated when added new sensor

      // If we have external handler, find the legend item ID and notify
      if (onExternalLegendToggle) {
        const sectionConfig = sectionConfigGraphs?.find((g: any) => g.id === sectionType);
        const legendItem = sectionConfig?.legend?.find((item: any) => item.displayName === sensorName);
        if (legendItem?.id) {
          onExternalLegendToggle(legendItem.id, true);
        }
      }
    },
    [internalEnableSensor, onExternalLegendToggle, sectionConfigGraphs]
  );

  // Wrap disableSensor to notify external handler
  const disableSensor = useCallback(
    (sectionType: string, sensorName: string) => {
      internalDisableSensor(sectionType, sensorName);

      // If we have external handler, find the legend item ID and notify
      if (onExternalLegendToggle) {
        const sectionConfig = sectionConfigGraphs?.find((g: any) => g.id === sectionType);
        const legendItem = sectionConfig?.legend?.find((item: any) => item.displayName === sensorName);
        if (legendItem?.id) {
          onExternalLegendToggle(legendItem.id, false);
        }
      }
    },
    [internalDisableSensor, onExternalLegendToggle, sectionConfigGraphs]
  );

  // Use focus mode hook to handle save/restore logic
  useFocusMode({
    focusMode,
    focusedSensors,
    enabledSensors,
    sectionConfigGraphs: normalizedGraphs,
    enableSensor,
    disableSensor,
    clearLoadingSensor,
  });

  const memoizedQueryParams = useMemoizedObject(queryParams);

  const {
    data,
    isSectionLoading,
    error,
    forceReloadAllData,
    refetchSectionData,
    cancelAllRequests,
    cancelSectionRequest,
    isAnySectionLoading,
    isQueryParamsChanging,
    isSensorLoading: isSensorLoadingFromFetch,
    hasCachedDataForSensor,
  } = useFetchData({
    queryParams: memoizedQueryParams || {
      site_id: '',
      asset_id: '',
      component_id: null,
      subcomponent_id: null,
      asset_category: 'fixed',
      from_date: null,
      to_date: null,
      asset_name: '',
      asset_supplier: null,
      asset_model: null,
      data_layer: 'sensors',
      full_pull: false,
    },
    graphDefinitions,
    activeFeatures: activeFeatures,
    activeDataLayerFilters: activeDataLayerFilters,
    resetTrigger: triggerReset || false,
    currentEnabledSensors: enabledSensors,
    onLoadingStateChange,
    externalLegendStates: {},
    expandableLegendForApi,
    focusedSensors,
  });

  // Clear loading sensors when data finishes loading
  useLoadingSensorCleanup({
    data,
    isSectionLoading,
    clearLoadingSensor,
    loadingSensors,
  });

  // Notify parent when section data changes (for audio integration in Waveform)
  useEffect(() => {
    if (onDataChange && data) {
      Object.entries(data).forEach(([sectionId, sectionData]) => {
        if (sectionData) {
          onDataChange(sectionId, sectionData);
        }
      });
    }
  }, [data, onDataChange]);

  // Register sensor data so search filters can access enabled states and colors
  useSensorDataRegistration({
    registerSensorData,
    dataLayer,
    enabledSensors,
    data,
  });

  // Effect to handle force refresh from tab switching
  useForceRefresh({
    forceRefresh,
    forceReloadAllData,
  });

  // Use section interaction handlers hook
  const { handleToggleSensor, handleReset, handleClearAll } = useSectionInteractionHandlers({
    enabledSensors,
    enableSensor,
    disableSensor,
    toggleSensor,
    clearSensors,
    resetSensors,
    resetFeatures,
    sectionConfigGraphs,
    syncSectionLegends,
    focusMode,
    onFocus,
    cancelSectionRequest,
  });

  // Initialize vertical lines and interval lines hooks (needed before reset effect)
  // Use external verticalLinesState if provided, otherwise create internal state
  const internalVerticalLinesState = useVerticalLines();
  const actualVerticalLinesState = verticalLinesState || internalVerticalLinesState;

  const {
    verticalLines,
    loadingLines,
    addVerticalLine,
    removeVerticalLine,
    clearAllLines: clearAllLinesOriginal,
    updateAllVerticalLineValues,
    updateVerticalLine,
  } = actualVerticalLinesState;

  const { intervalLines, addIntervalLines, removeIntervalLine, clearIntervalLines } = useIntervalLines();

  // Use benchmark lines hook (manages automatic benchmark selection)
  // If external state is provided (from parent), use that. Otherwise use local hook.
  const internalBenchmarkState = useBenchmarkLines(verticalLines);
  const benchmarkLineBySection = externalBenchmarkLineBySection ?? internalBenchmarkState.benchmarkLineBySection;
  const setBenchmarkLineBySection =
    externalSetBenchmarkLineBySection ?? internalBenchmarkState.setBenchmarkLineBySection;

  // Use harmonic management hook (manages harmonic state and utilities)
  const {
    harmonicSelectionsByChart,
    setHarmonicSelectionsByChart,
    focusedHarmonicByChart,
    setFocusedHarmonicByChart,
    getHarmonicGroupCount,
    getAvailableHarmonicPrefixes,
  } = useHarmonicManagement({ verticalLines });

  // Use side bands management hook (manages side bands state and utilities)
  const {
    sideBandsSelectionsByChart,
    setSideBandsSelectionsByChart,
    getSideBandsGroupCount,
    getAvailableSideBandsPrefixes,
  } = useSideBandsManagement({ verticalLines });

  // Wrapper for clearAllLines that also clears focused harmonic state
  const clearAllLines = useCallback(() => {
    clearAllLinesOriginal();
    // Clear all focused harmonics when clearing all lines
    setFocusedHarmonicByChart({});
  }, [clearAllLinesOriginal]);

  // Wrapper for addVerticalLine that limits user markers to 3
  const addVerticalLineWithLimit = useCallback(
    (...args: any[]) => {
      // Count current user markers (non-harmonic markers)
      const userMarkers = verticalLines.filter((line: any) => !line.isHarmonic && line.markerType === 'user');

      // Check if we're adding a user marker (not harmonic)
      const markerType = args[9]; // markerType is the 10th argument (index 9)
      const isHarmonic = args[5]; // isHarmonic is the 6th argument (index 5)

      // Only enforce limit for user markers (not system markers or harmonics)
      if (markerType === 'user' && !isHarmonic && userMarkers.length >= 3) {
        toast({
          title: 'Maximum timestamps reached',
          description: 'You can only add up to 3 timestamps. Please remove an existing timestamp first.',
        });
        return;
      }

      // Proceed with adding the marker
      addVerticalLine(...args);
    },
    [verticalLines, addVerticalLine]
  );

  // Auto-set first user marker as benchmark across all sections
  useAutoBenchmark({
    verticalLines,
    sectionConfigGraphs,
    setBenchmarkLineBySection,
  });

  // Auto-update benchmark when markers are removed
  useEffect(() => {
    // For each section, check if its benchmark still exists
    const updates: Record<string, string | null> = {};
    Object.entries(benchmarkLineBySection).forEach(([sectionKey, benchmarkId]) => {
      if (benchmarkId) {
        // Check if this benchmark line still exists
        const exists = verticalLines.some((l: VerticalLine) => l.id === benchmarkId);
        if (!exists) {
          // Benchmark was deleted, clear it
          updates[sectionKey] = null;
        }
      }
    });

    // Apply updates if any
    if (Object.keys(updates).length > 0) {
      setBenchmarkLineBySection((prev) => ({ ...prev, ...updates }));
    }
  }, [verticalLines, benchmarkLineBySection]);

  // Create highlighted version of verticalLines for selected marker
  const verticalLinesWithHighlight = useHighlightedVerticalLines({
    verticalLines,
    selectedMarkerTimestamp,
    selectedTimestampsSet,
  });

  // Handle data layer reset logic
  useDataLayerReset({
    triggerReset,
    preserveVerticalLinesOnReset,
    loadingSensors,
    initialFeatures,
    cancelAllRequests,
    clearLoadingSensor,
    clearAllLines,
    clearIntervalLines,
    resetAllFeatures,
    forceResetToInitial,
    onResetDataLayer,
    forceReloadAllData,
  });

  // Helper function to count HARMONIC groups only (H, I, J prefixes)
  // Returns number of unique H/I/J prefixes in use for this section
  const { showIntervalDialog, setShowIntervalDialog, handleShowIntervalDialog, handleEditThresholds, intervalVariant } =
    useDialogState();
  const {
    selectionMode,
    selectedRanges,
    addSelectedRange,
    removeSelectedRange,
    globalSelectionId,
    syncSelectionToChart,
  } = useSelectionMode();
  const { expandedSections, splitCharts, showModal, toggleSection, toggleSplitCharts, handleShowModal } =
    useChartSections(availableSections, splitDefault);

  // DEBUG logs removed for performance

  const { handleAddSelectedRange, handleRemoveSelectedRange } = useSelectionHandlers(
    addSelectedRange,
    removeSelectedRange
  );
  const { handleRangeChange, sharedTimeRange } = useSharedTimeRangeController();
  const { activeChartIds } = useChartIds({
    sectionConfig,
    availableSections,
    expandedSections,
    splitCharts,
    activeFeatures,
    selectedRanges,
    globalSelectionId,
    syncSelectionToChart,
  });

  const handleSensorAddition = useCallback(
    (sectionType: string, sensorName: string) => {
      // In sync mode, enable the sensor in ALL sections that have it
      if (syncSectionLegends) {
        const allSectionTypes: string[] = sectionConfigGraphs.map((g: any) => g.id);

        if (focusMode) {
          // Focus mode: Always focus the clicked sensor

          // If sensor is disabled, enable it first (will trigger data fetch)
          const isDisabled = allSectionTypes.some((section: string) => {
            return (
              enabledSensors[section] && sensorName in enabledSensors[section] && !enabledSensors[section][sensorName]
            );
          });

          if (isDisabled) {
            allSectionTypes.forEach((section: string) => {
              if (enabledSensors[section] && sensorName in enabledSensors[section]) {
                enableSensor(section, sensorName);
              }
            });
          }

          // Call onFocus to set this sensor as focused
          if (onFocus) {
            onFocus(sensorName, sectionType);
          }
        } else {
          // Multi-select mode: Enable sensor across all sections

          allSectionTypes.forEach((section: string) => {
            // Check if this sensor exists in this section
            if (enabledSensors[section] && sensorName in enabledSensors[section]) {
              enableSensor(section, sensorName);
            } else {
            }
          });
        }
      } else {
        // Non-sync mode
        if (focusMode) {
          // Focus mode: Always focus the clicked sensor

          // If sensor is disabled, enable it first (will trigger data fetch)
          const currentState = enabledSensors[sectionType]?.[sensorName];
          if (!currentState) {
            enableSensor(sectionType, sensorName);
          }

          // Call onFocus to set this sensor as focused
          if (onFocus) {
            onFocus(sensorName, sectionType);
          }
        } else {
          // Multi-select mode: just enable in the specified section
          enableSensor(sectionType, sensorName);
        }
      }
    },
    [enableSensor, disableSensor, syncSectionLegends, focusMode, sectionConfigGraphs, enabledSensors, onFocus]
  );

  // Register sensor toggle handler so search filters can enable/disable sensors
  useSensorToggleRegistration({
    registerSensorToggleHandler,
    dataLayer,
    enabledSensors,
    disableSensor,
    handleSensorAddition,
  });

  // Use vertical line values hook to update marker values based on sensor data
  useVerticalLineValues({
    verticalLines,
    data,
    enabledSensors,
    updateAllVerticalLineValues,
  });

  // Use section filter handlers hook
  const { handleFeatureChangeWithRefetch } = useSectionFilterHandlers({
    sectionHasFeatures,
    handleFeatureChange,
    cancelSectionRequest,
    setLoadingFeatures,
  });

  // Use harmonic group menu handlers hook
  const {
    handleEdit: handleHarmonicEdit,
    handleRemove: handleHarmonicRemove,
    handleFocus: handleHarmonicFocus,
  } = useHarmonicGroupMenuHandlers({
    selectedHarmonicLineId,
    verticalLines,
    harmonicMenuPosition,
    focusedHarmonicByChart,
    setSelectedHarmonicLineId,
    setHarmonicMenuPosition,
    setEditingHarmonicGroup,
    setEditingCarrierGroup,
    setFocusedHarmonicByChart,
    removeVerticalLine,
  });

  // Use vertical line menu handlers hook
  const {
    handleSetAsBenchmark: handleVerticalLineSetAsBenchmark,
    handleSetAsHarmonics: handleVerticalLineSetAsHarmonics,
    handleSetAsSideBands: handleVerticalLineSetAsSideBands,
    handleRemove: handleVerticalLineRemove,
  } = useVerticalLineMenuHandlers({
    selectedVerticalLineId,
    clickedChartId,
    verticalLines,
    verticalLineMenuPosition,
    data,
    sectionConfigGraphs,
    replaceMarkersOnHarmonicCreation,
    setSelectedVerticalLineId,
    setVerticalLineMenuPosition,
    setBenchmarkLineBySection,
    setEditingHarmonicGroup,
    setPendingHarmonicCreation,
    removeVerticalLine,
    getHarmonicGroupCount,
    getAvailableHarmonicPrefixes,
    getSideBandsGroupCount,
    getHarmonicInterval,
  });

  // Use harmonic edit dialog handler hook
  const { handleSave: handleHarmonicEditSave } = useHarmonicEditDialogHandler({
    editingHarmonicGroup,
    verticalLines,
    replaceMarkersOnHarmonicCreation,
    removeVerticalLine,
    addVerticalLine: addVerticalLineWithLimit,
  });

  // Use side bands edit dialog handler hook
  const { handleSave: handleSideBandsEditSave } = useSideBandsEditDialogHandler({
    editingSideBandsGroup,
    verticalLines,
    removeVerticalLine,
    addVerticalLine: addVerticalLineWithLimit,
  });

  // Handlers for custom vertical line menu
  const handleCustomLineFocusGroup = useCallback(() => {
    if (customLineMenuState.lineData) {
      setFocusedCustomGroupId(customLineMenuState.lineData.groupId);
    }
  }, [customLineMenuState.lineData]);

  const handleCustomLineUnfocusGroup = useCallback(() => {
    setFocusedCustomGroupId(null);
  }, []);

  const handleCustomLineSaveToDatabase = useCallback(async () => {
    if (customLineMenuState.lineData) {
      toast({
        title: 'Line saved',
        description: `Custom line ${customLineMenuState.lineData.label} has been saved to database`,
      });
    }
  }, [customLineMenuState.lineData]);

  const handleCustomLineRemove = useCallback(() => {
    if (customLineMenuState.lineData) {
      // Remove the line from visualization
      removeVerticalLine(customLineMenuState.lineData.id);
      toast({
        title: 'Line removed',
        description: `Custom line ${customLineMenuState.lineData.label} has been removed`,
      });
    }
  }, [customLineMenuState.lineData, removeVerticalLine]);

  const handleVerticalLineEdit = useCallback(
    (newLabel: string) => {
      if (selectedVerticalLineId && newLabel.trim()) {
        // Format label for markers/timestamps: uppercase and limit to 4 characters
        const formattedLabel = newLabel.trim().toUpperCase().substring(0, 4);
        updateVerticalLine(selectedVerticalLineId, { label: formattedLabel });
      }
    },
    [selectedVerticalLineId, updateVerticalLine]
  );

  const handleCustomLineEdit = useCallback(
    (updates: {
      label?: string;
      description?: string;
      mediaType?: 'image' | 'video';
      mediaUrl?: string;
      thumbnailUrl?: string;
      severity?: 'info' | 'low' | 'medium' | 'high' | 'critical';
      editedAt?: string;
      editedBy?: string;
    }) => {
      const newLabel = updates.label;
      if (customLineMenuState.lineData && newLabel && newLabel.trim()) {
        // Check if this is a user-created line (in verticalLines array) or system line (from customVerticalLines.groups)
        const lineInVerticalLines = verticalLines.find((vl: any) => vl.id === customLineMenuState.lineData.id);

        if (lineInVerticalLines) {
          // User-created line: update in verticalLines array
          updateVerticalLine(customLineMenuState.lineData.id, { label: newLabel });
        } else {
          // System line: we need to update the encoded label format

          // For user lines with encoded format "U0|unicorn-events|👤 Username", update the first part
          const lineData = customLineMenuState.lineData;
          if (lineData.label && lineData.label.includes('|')) {
            const labelParts = lineData.label.split('|');
            const newEncodedLabel = `${newLabel}|${labelParts[1]}|${labelParts[2] || ''}`;

            // Try to find and update by the encoded label format
            const lineByEncodedLabel = verticalLines.find((vl: any) => vl.label === lineData.label);
            if (lineByEncodedLabel) {
              updateVerticalLine(lineByEncodedLabel.id, { label: newEncodedLabel });
            }
          }

          // TODO: For true system lines from customVerticalLines.groups,
          // we might need to update the API data source, but for now this handles user-added lines
        }

        // Update the menu state to reflect the change immediately
        setCustomLineMenuState((prev) => ({
          ...prev,
          lineData: prev.lineData
            ? {
                ...prev.lineData,
                label: newLabel,
              }
            : null,
        }));
      }
    },
    [customLineMenuState.lineData, updateVerticalLine, verticalLines]
  );

  const metricsFromFeatures = useCallback(
    (
      sectionKey: string,
      sectionConfig: Record<string, any>,
      allActiveFeatures: Record<string, Record<string, boolean>>
    ): Metric[] => {
      const config = sectionConfig[sectionKey as keyof typeof sectionConfig];

      const colors =
        config?.legend?.length > 0
          ? config.legend.map((n: any, i: number) => [
              n.displayName,
              masterSensorColorMap?.[n.displayName] || config?.colors[i] || '#ccc',
            ])
          : [];

      return config
        ? [
            {
              key: sectionKey,
              label: config.title,
              unit: config.unit || '',
              show: true,
              sensorColors: colors.length > 0 ? Object.fromEntries(colors) : {},
              icon: <TrendingUp className="w-4 h-4" />,
            },
          ]
        : [];
    },
    [masterSensorColorMap]
  );

  // Register sensor data so search filters can access enabled states and colors
  useEffect(() => {
    if (registerSensorData && dataLayer && enabledSensors && data) {
      // Combine all enabled sensors from all sections in this data layer
      const combinedEnabledSensors: Record<string, boolean> = {};
      const combinedSensorColors: Record<string, string> = {};

      Object.values(enabledSensors).forEach((sectionSensors) => {
        Object.assign(combinedEnabledSensors, sectionSensors);
      });

      // Get sensor colors from the data
      Object.values(data || {}).forEach((sectionData) => {
        if (sectionData?.sensorColors) {
          Object.assign(combinedSensorColors, sectionData.sensorColors);
        }
      });

      registerSensorData(dataLayer, combinedEnabledSensors, combinedSensorColors);
    }
  }, [registerSensorData, dataLayer, enabledSensors, data]);

  const sectionKeys = Object.keys(sectionConfig);

  // Render section using DataLayerSection component
  const renderSection = (sectionKey: string) => {
    return (
      <DataLayerSection
        key={sectionKey}
        sectionKey={sectionKey}
        sectionConfig={sectionConfig}
        activeFeatures={activeFeatures}
        data={data}
        sectionConfigGraphs={sectionConfigGraphs}
        masterSensorColorMap={masterSensorColorMap}
        dataLayer={dataLayer}
        enabledSections={enabledSections}
        enabledSensors={enabledSensors}
        expandedSections={expandedSections}
        showModal={showModal}
        splitCharts={splitCharts}
        loadingFeatures={loadingFeatures}
        sharedTimeRange={sharedTimeRange}
        selectionMode={selectionMode}
        selectedRanges={selectedRanges}
        verticalLines={verticalLines}
        verticalLinesWithHighlight={verticalLinesWithHighlight}
        intervalLines={intervalLines}
        loadingLines={loadingLines}
        focusedSensors={focusedSensors}
        focusMode={focusMode}
        syncSectionLegends={syncSectionLegends}
        syncLegendComparisonTable={syncLegendComparisonTable}
        harmonicSelectionModeByChart={harmonicSelectionModeByChart}
        harmonicSelectionsByChart={harmonicSelectionsByChart}
        focusedHarmonicByChart={focusedHarmonicByChart}
        benchmarkLineBySection={benchmarkLineBySection}
        selectedHarmonicLineId={selectedHarmonicLineId}
        showPercentage={showPercentage}
        showPercentageBySection={showPercentageBySection}
        showTableView={showTableView}
        showTableViewBySection={showTableViewBySection}
        setShowPercentage={setShowPercentage}
        setShowPercentageBySection={setShowPercentageBySection}
        setShowTableView={setShowTableView}
        setShowTableViewBySection={setShowTableViewBySection}
        selectedTimestampsSet={selectedTimestampsSet}
        selectedMarkerTimestamp={selectedMarkerTimestamp}
        expandableLegendSeries={expandableLegendSeries}
        expandableLegendFocusMode={expandableLegendFocusMode}
        enabledSeries={enabledSeries}
        popoverTimestamps={popoverTimestamps}
        onToggleExpandableSeries={onToggleExpandableSeries}
        onToggleExpandableSeriesFocus={onToggleExpandableSeriesFocus}
        onToggleExpandableTimestamp={onToggleExpandableTimestamp}
        onRemoveMarker={onRemoveMarker}
        metricsFromFeatures={metricsFromFeatures}
        isSectionLoading={isSectionLoading}
        getSectionMultiSelect={getSectionMultiSelect}
        sectionHasFeatures={sectionHasFeatures}
        toggleSection={toggleSection}
        toggleSectionMultiSelect={toggleSectionMultiSelect}
        handleFeatureChangeWithRefetch={handleFeatureChangeWithRefetch}
        toggleSplitCharts={toggleSplitCharts}
        clearAllLines={clearAllLines}
        handleShowModal={handleShowModal}
        handleShowIntervalDialog={handleShowIntervalDialog}
        clearIntervalLines={clearIntervalLines}
        handleToggleSensor={handleToggleSensor}
        handleSensorAddition={handleSensorAddition}
        isSensorLoadingFromFetch={isSensorLoadingFromFetch}
        hasCachedDataForSensor={hasCachedDataForSensor}
        addVerticalLine={addVerticalLineWithLimit}
        removeVerticalLine={removeVerticalLine}
        handleRangeChange={handleRangeChange}
        handleReset={handleReset}
        handleClearAll={handleClearAll}
        handleEditThresholds={handleEditThresholds}
        removeIntervalLine={removeIntervalLine}
        handleAddSelectedRange={handleAddSelectedRange}
        handleRemoveSelectedRange={handleRemoveSelectedRange}
        cancelSectionRequest={cancelSectionRequest}
        addIntervalLines={addIntervalLines}
        onFocus={onFocus}
        onToggleFocusMode={onToggleFocusMode}
        setHarmonicSelectionsByChart={setHarmonicSelectionsByChart}
        setHarmonicSelectionModeByChart={setHarmonicSelectionModeByChart}
        setSideBandsSelectionModeByChart={setSideBandsSelectionModeByChart}
        setMarkerSelectionModeByChart={setMarkerSelectionModeByChart}
        markerSelectionModeByChart={markerSelectionModeByChart}
        sideBandsSelectionModeByChart={sideBandsSelectionModeByChart}
        customLineModeByChart={customLineModeByChart}
        setCustomLineModeByChart={setCustomLineModeByChart}
        enableCompareTable={enableCompareTable}
        enableMarkers={enableMarkers}
        enableHarmonics={enableHarmonics}
        enableSideBands={enableSideBands}
        enableDrawing={enableDrawing}
        enableHarmonicEdit={enableHarmonicEdit}
        enableSideBandsEdit={enableSideBandsEdit}
        replaceMarkersOnHarmonicCreation={replaceMarkersOnHarmonicCreation}
        markerLabel={markerLabel}
        expandableLegendLabel={expandableLegendLabel}
        showFloatingToolbar={showFloatingToolbar}
        syncTools={syncTools}
        getHarmonicGroupCountCallback={getHarmonicGroupCount}
        getAvailableHarmonicPrefixesCallback={getAvailableHarmonicPrefixes}
        getSideBandsGroupCountCallback={getSideBandsGroupCount}
        getAvailableSideBandsPrefixesCallback={getAvailableSideBandsPrefixes}
        getHarmonicIntervalCallback={getHarmonicInterval}
        pendingHarmonicCreation={pendingHarmonicCreation}
        setSelectedHarmonicLineId={setSelectedHarmonicLineId}
        setHarmonicMenuPosition={setHarmonicMenuPosition}
        setSelectedVerticalLineId={setSelectedVerticalLineId}
        setVerticalLineMenuPosition={setVerticalLineMenuPosition}
        setClickedChartId={setClickedChartId}
        setEditingHarmonicGroup={setEditingHarmonicGroup}
        setEditingSideBandsGroup={setEditingCarrierGroup}
        setPendingHarmonicCreation={setPendingHarmonicCreation}
        setBenchmarkLineBySection={setBenchmarkLineBySection}
        setFocusedHarmonicByChart={setFocusedHarmonicByChart}
        showInlineFocusButton={showInlineFocusButton}
        showSectionControls={showSectionControls}
        isExpandable={isExpandable}
        setCustomLineMenuState={setCustomLineMenuState}
        focusedCustomGroupId={focusedCustomGroupId}
        showYAxisScale={showYAxisScale}
        enableDynamicMargin={enableDynamicMargin}
        autoscaleMode={autoscaleMode}
        autoscaleAxis={autoscaleAxis}
        loadingSensors={loadingSensors}
        isTimestampsLoading={isTimestampsLoading}
      />
    );
  };

  // Clean up pending requests on unmount
  useRequestCleanup({
    cancelAllRequests,
  });

  const content = (
    <div className="min-h-0">
      {selectionMode && <SelectionModeCard selectedRanges={selectedRanges} activeChartIds={activeChartIds} />}
      {sectionKeys.filter((sectionKey) => enabledSections[sectionKey]).map(renderSection)}
      <IntervalDialog
        open={showIntervalDialog}
        onClose={() => setShowIntervalDialog(false)}
        intervalLines={intervalLines}
        onAddIntervalLines={addIntervalLines}
        onRemoveIntervalLine={removeIntervalLine}
        variant={intervalVariant}
      />
      <HarmonicGroupMenu
        open={selectedHarmonicLineId !== null}
        onClose={() => {
          setSelectedHarmonicLineId(null);
          setHarmonicMenuPosition(null);
        }}
        position={harmonicMenuPosition || undefined}
        isFocused={(() => {
          if (!selectedHarmonicLineId) return false;
          const line = verticalLines.find((l: VerticalLine) => l.id === selectedHarmonicLineId);
          if (!line || !line.sectionKey) return false;
          return focusedHarmonicByChart[line.sectionKey] === line.groupId;
        })()}
        showEdit={(() => {
          if (!selectedHarmonicLineId) return true;
          const line = verticalLines.find((l: VerticalLine) => l.id === selectedHarmonicLineId);
          if (!line || !line.label || typeof line.label !== 'string') return true;
          // Check label prefix to determine if it's a carrier (C, D, E) or harmonic (H, I, J)
          const match = line.label.match(/^-?([A-Z])/);
          if (!match) return true;
          const prefix = match[1];
          const isCarrier = ['C', 'D', 'E'].includes(prefix);
          return isCarrier ? enableSideBandsEdit : enableHarmonicEdit;
        })()}
        onEdit={handleHarmonicEdit}
        onRemove={handleHarmonicRemove}
        onFocus={handleHarmonicFocus}
      />
      <HarmonicEditDialog
        open={(() => {
          const isOpen = editingHarmonicGroup !== null;
          return isOpen;
        })()}
        onClose={() => setEditingHarmonicGroup(null)}
        initialLabelPrefix={editingHarmonicGroup?.labelPrefix || 'H'}
        initialMultiplier={editingHarmonicGroup?.multiplier || 6}
        initialStartingPoint={editingHarmonicGroup?.startingPoint || 0}
        xAxisType={editingHarmonicGroup?.xAxisType || 'numeric'}
        initialIntervalType={editingHarmonicGroup?.intervalType || 'days'}
        position={editingHarmonicGroup?.position}
        replaceMarkers={replaceMarkersOnHarmonicCreation}
        isNewGroup={editingHarmonicGroup?.isNewGroup ?? false}
        onSave={handleHarmonicEditSave}
      />
      <SideBandsGroupEditDialog
        open={editingSideBandsGroup !== null}
        onClose={() => setEditingCarrierGroup(null)}
        initialLabelPrefix={editingSideBandsGroup?.labelPrefix || 'C'}
        initialCenterPosition={editingSideBandsGroup?.centerPosition || 0}
        initialSidebandPosition={editingSideBandsGroup?.sidebandPosition || 0}
        initialCount={editingSideBandsGroup?.count || 3}
        xAxisType={editingSideBandsGroup?.xAxisType || 'numeric'}
        position={editingSideBandsGroup?.menuPosition}
        replaceMarkers={replaceMarkersOnHarmonicCreation}
        isNewGroup={editingSideBandsGroup?.isNewGroup ?? false}
        onSave={handleSideBandsEditSave}
      />
      <VerticalLineMenu
        open={selectedVerticalLineId !== null}
        onClose={() => {
          setSelectedVerticalLineId(null);
          setVerticalLineMenuPosition(null);
        }}
        position={verticalLineMenuPosition || undefined}
        lineData={(() => {
          if (!selectedVerticalLineId) return undefined;
          const line = verticalLines.find((l: VerticalLine) => l.id === selectedVerticalLineId);
          if (!line) return undefined;

          // Extract clean label and username from encoded format if present
          let cleanLabel = line.label;
          let username = undefined;

          if (line.label && typeof line.label === 'string' && line.label.includes('|')) {
            const labelParts = line.label.split('|');
            cleanLabel = labelParts[0]; // e.g., "T3" -> just show "T3" for now, but we might want something better
            if (labelParts[2]) {
              username = labelParts[2].replace('👤 ', '');
            }
          } else {
            // For regular markers without encoded format, use current user
            username = user?.displayName || user?.email?.split('@')[0] || 'Current User';
          }

          return {
            id: line.id,
            label: cleanLabel,
            source: line.source || 'user',
            username: username,
            timestamp: line.timestamp,
            deletable: (line as any).deletable,
            dimmed: (line as any).dimmed,
          };
        })()}
        onEdit={undefined} // No edit for regular markers
        isBenchmark={(() => {
          // Check if the selected line is the benchmark for ANY section
          // Markers are global (no sectionKey), so we check all sections
          if (!selectedVerticalLineId) {
            return false;
          }
          const line = verticalLines.find((l: VerticalLine) => l.id === selectedVerticalLineId);
          if (!line) {
            return false;
          }

          // Check if ANY section has this marker as its benchmark
          // Since markers are global (no sectionKey), they can be benchmarks for multiple sections
          const isBench = Object.values(benchmarkLineBySection).includes(selectedVerticalLineId);
          return isBench;
        })()}
        canRemove={(() => {
          if (!selectedVerticalLineId) return true;
          const line = verticalLines.find((l: VerticalLine) => l.id === selectedVerticalLineId);
          if (!line) return true;
          // Hide remove only if deletable is explicitly false
          // Show remove for user-created markers (deletable undefined) and deletable === true
          return (line as any).deletable !== false;
        })()}
        onSetAsBenchmark={handleVerticalLineSetAsBenchmark}
        onSetAsHarmonics={handleVerticalLineSetAsHarmonics}
        onSetAsSideBands={handleVerticalLineSetAsSideBands}
        onRemove={handleVerticalLineRemove}
        onDim={() => {
          if (!selectedVerticalLineId) return;
          const line = verticalLines.find((l: VerticalLine) => l.id === selectedVerticalLineId);
          if (!line) return;
          // Toggle dimmed state
          updateVerticalLine(selectedVerticalLineId, { dimmed: !(line as any).dimmed } as any);
        }}
        enableCompareTable={enableCompareTable}
        enableHarmonics={enableHarmonics}
        enableSideBands={enableSideBands}
      />
      <CustomVerticalLineMenu
        open={customLineMenuState.open}
        onClose={() => setCustomLineMenuState({ ...customLineMenuState, open: false })}
        onFocusGroup={handleCustomLineFocusGroup}
        onUnfocusGroup={
          focusedCustomGroupId === customLineMenuState.lineData?.groupId ? handleCustomLineUnfocusGroup : undefined
        }
        onSaveToDatabase={handleCustomLineSaveToDatabase}
        onRemove={customLineMenuState.lineData?.source !== 'system' ? handleCustomLineRemove : undefined}
        onDim={() => {
          const lineId = customLineMenuState.lineData?.id;
          if (!lineId) return;
          const line = verticalLines.find((l: VerticalLine) => l.id === lineId);
          if (!line) return;
          // Toggle dimmed state for single line
          updateVerticalLine(lineId, { dimmed: !(line as any).dimmed } as any);
        }}
        onDimGroup={() => {
          const groupId = customLineMenuState.lineData?.groupId;
          if (!groupId) return;
          // Find all lines in this group
          const groupLines = verticalLines.filter(
            (l: VerticalLine) => (l as any).groupId === groupId || (l as any)._systemGroupId === groupId
          );
          if (groupLines.length === 0) return;
          // Check if ANY line in group is dimmed - if so, undim all; otherwise dim all
          // This matches the display logic: groupDimmed = some((l) => l.dimmed)
          // User expectation: if group was dimmed and one was undimmed, "Undim Group" should clear all dimming
          const anyDimmed = groupLines.some((l: VerticalLine) => (l as any).dimmed);
          const newDimmedValue = !anyDimmed; // If any dimmed → undim all (false); if none dimmed → dim all (true)
          // Update all lines in the group
          groupLines.forEach((line: VerticalLine) => {
            updateVerticalLine(line.id, { dimmed: newDimmedValue } as any);
          });
        }}
        onEdit={handleCustomLineEdit}
        position={customLineMenuState.position}
        lineData={
          customLineMenuState.lineData
            ? {
                ...customLineMenuState.lineData,
                // Compute groupDimmed: true if ANY line in the group is dimmed
                // User expectation: if some lines are still dimmed, show "Undim Group" to clear all dimming
                groupDimmed: (() => {
                  const groupId = customLineMenuState.lineData?.groupId;
                  if (!groupId) return false;
                  const groupLines = verticalLines.filter(
                    (l: VerticalLine) => (l as any).groupId === groupId || (l as any)._systemGroupId === groupId
                  );
                  return groupLines.length > 0 && groupLines.some((l: VerticalLine) => (l as any).dimmed);
                })(),
              }
            : undefined
        }
        isFocused={focusedCustomGroupId === customLineMenuState.lineData?.groupId}
        canRemove={customLineMenuState.lineData?.deletable !== false}
      />
    </div>
  );

  // Wrap with DrawingProvider if drawing is enabled
  if (enableDrawing) {
    return <DrawingProvider>{content}</DrawingProvider>;
  }

  return content;
};
